import {
  isAppointment,
  isCancelledStatus,
  isCompletedStatus,
  isOpenSlot,
} from "@/features/appointments/utils";
import { isActiveStatus } from "@/features/appointments/utils/isActiveStatus";
import type { CalenderAppointmentsSearchFilters } from "@/features/calendar/calendarFilter/appointmentFilterReducer";
import { type CalendarFilters } from "@repo/api";
import {
  type Appointment,
  type AppointmentLocationType,
  type AppointmentStage,
  type AppointmentStatus,
  type AppointmentsSearchResponse,
  type CalendarAppointmentsSearchResponse,
  type ParentTaskType,
  type Schedule,
  type SubTaskType,
  type Task,
  isTask,
} from "@repo/api";
import {
  LightMode,
  documentIcon,
  documentPencilIcon,
  mailIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  type ActivityConfig,
  CustomColors,
  type Settings,
  type TaskConfig,
  getTaskColor,
  getTaskIcon,
  makeAlphaColor,
  translateServices,
} from "@repo/ui";
import { isDefined, logError } from "@repo/utils";
import {
  addMinutes,
  differenceInHours,
  differenceInMinutes,
  endOfDay,
  isBefore,
  isSameMinute,
  max,
  min,
  startOfDay,
} from "date-fns";
import isEmpty from "lodash/isEmpty";

import { AppointmentConfiguration } from "./appointmentConfiguration";
import {
  LAST_HOUR,
  LENGTH_TASKS_MINUTES,
  UNASSIGNED_CALENDAR_EVENT,
  UNASSIGNED_OPEN_SLOT_CALENDAR_EVENT,
} from "./constants";
import { BLOCK_MIN_WIDTH } from "./team-calendar/constants";
import {
  type CalendarView,
  type CoWorkerCalendarBokaAppointment,
  type CoWorkerCalendarItem,
  type CoWorkerCalendarOutlook,
  type CoWorkerCalendarTask,
  type CoWorkerScheduleActivity,
  type OutlookEvent,
  isAbsenceScheduleActivity,
  isCoWorkerCalendarBokaAppointment,
  isCoWorkerCalendarOutlook,
  isCoWorkerCalendarTask,
  isCoWorkerScheduleActivity,
} from "./types";
import { isOutlookEvent } from "./useUserCalendar";
import { WEEK_BLOCK_MIN_HEIGHT } from "./week-calendar/constants";

const DEFAULT_CALENDAR_COLOR = {
  color: LightMode.colourStaticIkeaBrandBlue,
  borderColor: LightMode.colourStaticIkeaBrandBlue,
  textColor: LightMode.colourTextAndIcon1,
};
const CALENDAR_DEFAULT_ALPHA_VALUE = "0.2";

const outlookColorMap = {
  icon: mailIcon,
  color: makeAlphaColor(CustomColors.outlookBlue, CALENDAR_DEFAULT_ALPHA_VALUE),
  borderColor: CustomColors.outlookBlue,
  textColor: LightMode.colourTextAndIcon1,
};

const isOutlookEventTentative = (appointment: {
  showAs?: OutlookEvent["showAs"];
  responseStatus?: OutlookEvent["responseStatus"];
}) => {
  return (
    appointment.showAs === "tentative" ||
    appointment.responseStatus?.response === "declined" ||
    appointment.responseStatus?.response === "notResponded"
  );
};

interface RenderTypeInfo {
  appointmentType: "task" | "appointment" | "outlook" | "scheduleActivity";
  active: boolean;
  tentative: boolean;
  parentType?: ParentTaskType;
  subType?: SubTaskType;
  locationType?: AppointmentLocationType;
  service?: string;
  status?: AppointmentStatus;
  tasks?: Task[];
  videoLinkCoworker?: string;
  isDropIn?: boolean;
  walkIn?: boolean;
  activity?: string;
}

export const getRenderTypeInfo = (
  appointment: Task | Appointment | OutlookEvent | CoWorkerScheduleActivity
): RenderTypeInfo | undefined => {
  if (isScheduleActivity(appointment)) {
    return {
      appointmentType: "scheduleActivity" as const,
      parentType: appointment.parentType,
      subType: appointment.subType,
      active: true,
      tentative: false,
      activity: appointment.activity,
    };
  } else if (isAppointment(appointment)) {
    return {
      appointmentType: "appointment" as const,
      isDropIn: appointment.appointmentType === "DROP_IN",
      walkIn: appointment.appointmentType === "WALKIN_BOOKING",
      active: isActiveStatus(appointment.appointmentStatus),
      locationType: appointment.locationType,
      tentative: appointment.orderNumber === undefined,
      status: appointment.appointmentStatus,
      service: appointment.service,
      videoLinkCoworker: appointment.videoLinkCoworker,
    };
  } else if (isTask(appointment)) {
    return {
      appointmentType: "task" as const,
      active: !appointment.isDone,
      parentType: appointment.parentTaskType,
      subType: appointment.subTaskType,
      tentative: false,
    };
  } else if (isOutlookEvent(appointment)) {
    return {
      appointmentType: "outlook" as const,
      active: true,
      tentative: isOutlookEventTentative(appointment),
    };
  }
};

export const getRenderTypeInfoFromCalendarItem = (
  item: CoWorkerCalendarItem
): RenderTypeInfo | undefined => {
  if (isCoWorkerCalendarBokaAppointment(item)) {
    return {
      appointmentType: "appointment" as const,
      active: isActiveStatus(item.status),
      locationType: item.locationType,
      tentative:
        item.emailId === undefined ||
        item.emailId === "" ||
        isOpenSlot(item.reasonCode, item.orderNumber),
      status: item.status,
      service: item.service,
      tasks: item.tasks,
      videoLinkCoworker: item.videoLinkCoworker,
      isDropIn: item.isDropIn,
      walkIn: item.walkIn,
    };
  } else if (isCoWorkerCalendarTask(item)) {
    return {
      appointmentType: "task" as const,
      active: item.active ?? true,
      parentType: item.parentType,
      subType: item.subType,
      tentative: false,
    };
  } else if (isCoWorkerCalendarOutlook(item)) {
    return {
      appointmentType: "outlook" as const,
      active: item.active,
      tentative: isOutlookEventTentative({
        responseStatus: { response: item.responseStatus },
        showAs: item.showAs,
      }),
    };
  } else if (isCoWorkerScheduleActivity(item)) {
    return {
      appointmentType: "scheduleActivity" as const,
      parentType: item.parentType,
      subType: item.subType,
      active: true,
      tentative: false,
      activity: item.activity,
    };
  }
};

const getAppointmentStage = (item: RenderTypeInfo): AppointmentStage => {
  if (item.service?.startsWith("PLANNING")) {
    return "PLANNING";
  }
  if (item.service?.startsWith("VERIFY")) {
    return "VERIFICATION";
  }
  if (item.service?.startsWith("ORDER")) {
    return "ORDER";
  }
  if (item.tentative) {
    return "OPEN";
  }

  return "PLANNING";
};

export const getColor = (
  item?: RenderTypeInfo,
  taskConfigs?: TaskConfig[],
  activityConfig?: ActivityConfig[]
) => {
  let color = DEFAULT_CALENDAR_COLOR;
  if (item) {
    if (item.appointmentType === "outlook") {
      color = outlookColorMap;
    } else if (item.appointmentType === "task") {
      color = getTaskColor(
        item.parentType,
        item.active,
        item.subType,
        taskConfigs
      );
    } else if (item.appointmentType === "appointment") {
      color = {
        ...AppointmentConfiguration[getAppointmentStage(item)],
        textColor:
          isCancelledStatus(item.status) || isCompletedStatus(item.status)
            ? LightMode.colourTextAndIcon3
            : LightMode.colourTextAndIcon1,
      };

      if (item.walkIn) {
        color = {
          ...color,
          color: makeAlphaColor(
            CustomColors.walkInAppointment,
            CALENDAR_DEFAULT_ALPHA_VALUE
          ),
          borderColor: CustomColors.walkInAppointment,
        };
      }

      if (item.status === "STARTED") {
        color = {
          ...color,
          color: makeAlphaColor(color.color, "1.0"),
        };
      }
    } else if (item.appointmentType === "scheduleActivity") {
      const activityColor = activityConfig?.find(
        (a) => a.activity_key === item.activity
      )?.colour;
      if (activityColor) {
        color = {
          color: makeAlphaColor(
            activityColor.token,
            CALENDAR_DEFAULT_ALPHA_VALUE
          ),
          borderColor: activityColor?.token,
          textColor: LightMode.colourTextAndIcon1,
        };
      } else {
        color = getTaskColor(
          item.parentType,
          item.active,
          item.subType,
          taskConfigs
        );
      }
    }
  }

  if (isCancelledStatus(item?.status) || isCompletedStatus(item?.status)) {
    color = {
      ...color,
      borderColor: makeAlphaColor(color?.borderColor, "0.3"),
      textColor: LightMode.colourTextAndIcon3,
    };
  }

  return color ?? DEFAULT_CALENDAR_COLOR;
};

export const getIsDashed = (item?: RenderTypeInfo) => {
  if (!item) {
    return false;
  }

  if (item.isDropIn) {
    return false;
  }

  return item.tentative;
};

export const getIcon = (
  info?: RenderTypeInfo,
  isDone?: boolean,
  taskConfigs?: TaskConfig[]
) => {
  if (!info) {
    return documentPencilIcon;
  }

  let icon;

  if (info.appointmentType === "outlook") {
    return outlookColorMap.icon;
  }
  if (info.appointmentType === "task") {
    icon = getTaskIcon(info.parentType, info.subType, taskConfigs);
  } else if (info.appointmentType === "appointment") {
    const entry = AppointmentConfiguration[getAppointmentStage(info)];
    icon =
      info.locationType === "REMOTE" ||
      info.locationType === "STORE_REMOTE" ||
      !isEmpty(info.videoLinkCoworker)
        ? entry.remoteIcon
        : entry.icon;
  }

  return icon ?? (isDone ? documentPencilIcon : documentIcon);
};

export const getAppointmentTitle = (item: Appointment) => {
  return [
    item.type === "DROP_IN" ? t("label.drop_in") : undefined,
    isOpenSlot(item.reasonCode, item.orderNumber, item.type)
      ? t("label.unplanned_slot")
      : undefined,
    translateServices(item.service),
  ]
    .filter(isDefined)
    .join(" - ");
};

export const mapToCalendarItem = (
  items: (Appointment | Task | OutlookEvent)[],
  firstDateOfWeek?: Date,
  lastDateOfWeek?: Date
): CoWorkerCalendarItem[] => {
  return items
    .map((item) => {
      try {
        if (isAppointment(item)) {
          const startDate = new Date(item.timeWindowStartTime);
          const endDate = new Date(item.timeWindowEndTime);

          const res: CoWorkerCalendarBokaAppointment = {
            id: item.appointmentId ? item.appointmentId.toString() : item.id,
            appointmentType: "appointment" as const,
            allDay: false,
            isDueBy: false,
            start: startDate,
            end: endDate,
            title: getAppointmentTitle(item),
            opportunityId: item.opportunityId,
            opportunityTitle: item.opportunityTitle,
            locationType: item.locationType,
            orderNumber: item.orderNumber,
            active: isActiveStatus(item.appointmentStatus),
            emailId:
              !isDefined(item.assignedCoworker) ||
              isEmpty(item.assignedCoworker)
                ? UNASSIGNED_CALENDAR_EVENT
                : item.assignedCoworker.toLowerCase(),
            assignedTeam: item.assignedTeam,
            length: differenceInMinutes(endDate, startDate),
            status: item.appointmentStatus,
            reasonCode: item.reasonCode,
            customer: item.customer
              ? {
                  firstName: item.customer.firstName,
                  lastName: item.customer.lastName,
                }
              : undefined,
            plannerOpportunities: item.plannerOpportunities,
            opportunityDetail: item.opportunityDetail,
            tasks: item.tasks,
            location: item.locationName,
            service: item.service,
            paymentStatus: item.paymentStatus,
            questionsInfo: item.questionsInfo,
            internal: item.appointmentType === "INTERNAL_BOOKING",
            walkIn: item.appointmentType === "WALKIN_BOOKING",
            videoLinkCoworker: item.videoLinkCoworker,
            isDropIn: item.appointmentType === "DROP_IN",
          };

          return res;
        } else if (isTask(item)) {
          // We can't show tasks that don't have a start date
          if (!item.taskStartDateTime) {
            return null;
          }

          let startDate = firstDateOfWeek
            ? max([new Date(item.taskStartDateTime), firstDateOfWeek])
            : new Date(item.taskStartDateTime);
          const tempEndDate = item.taskEndDateTime
            ? new Date(item.taskEndDateTime)
            : addMinutes(
                new Date(item.taskStartDateTime),
                LENGTH_TASKS_MINUTES
              );
          const endDate = min([tempEndDate, lastDateOfWeek ?? tempEndDate]);
          const allDay = item.isTimeSet === false;
          const isDueBy = item.isTaskDueBy;

          // due date task
          if (isSameMinute(startDate, endDate)) {
            startDate = addMinutes(endDate, -60);
          }

          const res: CoWorkerCalendarTask = {
            id: item.id.toString(),
            appointmentType: "task" as const,
            allDay,
            isDueBy,
            start: startDate,
            end: endDate,
            title: item.title,
            defaultTitle: item.defaultTitle,
            opportunityId: item.opportunityId,
            opportunityTitle: undefined,
            parentType: item.parentTaskType,
            subType: item.subTaskType,
            active: !item.isDone,
            emailId: item.emailId?.toLowerCase() ?? UNASSIGNED_CALENDAR_EVENT,
            assignedTeam: item.assignedTeam,
            description: item.description,
            length: allDay ? 24 * 60 : differenceInMinutes(endDate, startDate),
          };

          return res;
        } else if (isOutlookEvent(item)) {
          let startDate = new Date(`${item.start.dateTime}+00:00`);
          let endDate = new Date(`${item.end.dateTime}+00:00`);

          if (item.isAllDay) {
            startDate = new Date(item.start.dateTime);
            // hack since all days in outlook crosses to the next date
            endDate = addMinutes(new Date(item.end.dateTime), -1);

            // outlook all days are strange and don't follow UTC as expected
            if (firstDateOfWeek && isBefore(endDate, firstDateOfWeek)) {
              return null;
            }
          }

          const res: CoWorkerCalendarOutlook = {
            id: item.id,
            appointmentType: "outlook" as const,
            allDay: item.isAllDay,
            isDueBy: false,
            start: startDate,
            end: endDate,
            title: item.subject,
            description: item.bodyPreview,
            active: true,
            emailId: UNASSIGNED_CALENDAR_EVENT,
            length: differenceInMinutes(endDate, startDate),
            showAs: item.showAs,
            sensitivity: item.sensitivity,
            attendees: item.attendees,
            onlineMeeting: item.onlineMeeting,
            location: item.location,
            responseStatus: item.responseStatus?.response,
            isCancelled: item.isCancelled,
            webLink: item.webLink,
          };

          return res;
        }
      } catch (e) {
        // Catch invalid items, log them and filter out the broken items
        logError(e, [{ tagName: "id", tagValue: item?.id?.toString() }]);
      }
      return undefined;
    })
    .filter(isDefined)
    .sort((a, b) => {
      return a.start.getTime() - b.start.getTime();
    });
};

export const getCalStartHour = (
  sortedItems: CoWorkerCalendarItem[],
  selectedDate?: Date
) => {
  let first = 7;
  const selectedDayStart = selectedDate ? startOfDay(selectedDate) : undefined;

  for (const item of sortedItems) {
    if (item.allDay) {
      continue;
    }

    if (selectedDayStart && item.start < selectedDayStart) {
      first = 0;
      break;
    }

    if (item.start.getHours() < first) {
      first = item.start.getHours();
    }
  }

  return { calStartHour: first, totalHours: LAST_HOUR - first };
};

export const isOffHour = (
  item: CoWorkerCalendarItem,
  selectedDate: Date,
  startHour: number,
  endHour: number
) => {
  if (item.allDay) {
    return false;
  }

  const calendarStart = new Date(selectedDate);
  calendarStart.setHours(startHour, 0, 0, 0);
  const calendarEnd = new Date(selectedDate);
  calendarEnd.setHours(endHour, 0, 0, 0);

  return item.end <= calendarStart || item.start >= calendarEnd;
};

export const getBlockHeight = (
  item: CoWorkerCalendarItem,
  blockLength: number
) => {
  const displayStart = item.displayStart ?? item.start;
  const maxHeight = differenceInMinutes(endOfDay(displayStart), displayStart);
  const hours = differenceInHours(endOfDay(displayStart), displayStart); // grid line
  const calculatedHeight =
    (Math.min(maxHeight + hours, item.length) * blockLength) / 60;
  return Math.max(calculatedHeight, WEEK_BLOCK_MIN_HEIGHT);
};

export const getBlockLength = (
  length: number,
  blockLength: number,
  maxHours: number
) => {
  const maxLength = maxHours * 60;
  const calculatedLength = (Math.min(maxLength, length) * blockLength) / 60;

  return Math.max(calculatedLength, BLOCK_MIN_WIDTH);
};

export const allowDragAndDrop = (
  item: CoWorkerCalendarItem,
  calendarView: CalendarView
) => {
  // The "Unassigned open slots" lane is a read-only display for now: items
  // inside it must not be draggable, and by disabling their listeners we
  // also implicitly kill within-lane reordering.
  if (item.emailId === UNASSIGNED_OPEN_SLOT_CALENDAR_EVENT) {
    return false;
  }

  return (
    (isCoWorkerCalendarTask(item) && item.active) ||
    (isCoWorkerCalendarBokaAppointment(item) &&
      isActiveStatus(item.status) &&
      calendarView === "TEAM") ||
    isCoWorkerScheduleActivity(item)
  );
};

export const allowCopyPaste = (
  item: CoWorkerCalendarItem,
  calendarView: CalendarView
) =>
  (isCoWorkerCalendarTask(item) && item.active) ||
  (isCoWorkerCalendarBokaAppointment(item) &&
    isActiveStatus(item.status) &&
    calendarView === "TEAM");

export const allowResize = (item: CoWorkerCalendarItem & { resize: boolean }) =>
  !item.resize || !item.isDueBy;

export const getPreCallTaskStatus = (appointment?: RenderTypeInfo) => {
  const preCallTasks = appointment?.tasks?.filter(
    (task) => task.subTaskType === "preCallTask"
  );
  const isPreCallDone = preCallTasks?.every((task) => task.isDone);
  return { preCallTasks, isPreCallDone };
};

const mapCalendarAppointmentsResponseToAppointment = (
  calendarResponse: CalendarAppointmentsSearchResponse
): Appointment => {
  return {
    id: calendarResponse.id || "",
    appointmentId: calendarResponse.appointmentId,
    appointmentStatus: calendarResponse.appointmentStatus,
    assignedCoworker: calendarResponse.assignedCoworker?.toLowerCase() || "",
    assignedTeam: calendarResponse.assignedTeam,
    bookingTime: calendarResponse.bookingTime,
    service: calendarResponse.service || "",
    timeWindowStartTime: calendarResponse.timeWindowStartTime || "",
    timeWindowEndTime: calendarResponse.timeWindowEndTime || "",
    locationName: "",
    locationType: "",
    price: 0,
    customer: {
      firstName: calendarResponse.firstName || "",
      lastName: calendarResponse.lastName || "",
    },
    type: calendarResponse.appointmentType,
    appointmentLink: calendarResponse.appointmentLink,
    videoLinkCoworker: calendarResponse.videoLinkCoworker,
    videoLinkCustomer: calendarResponse.videoLinkCustomer,
    appointmentType: calendarResponse.bokaAppointmentType,
    orderNumber: calendarResponse.orderNumber,
    tasks: calendarResponse.tasks,
    previousOrderNumber: calendarResponse.previousOrderNumber || undefined,
    followupOrderNumber: calendarResponse.followupOrderNumber || undefined,
  };
};

export const getAppointments = (
  data:
    | CalendarAppointmentsSearchResponse[]
    | AppointmentsSearchResponse[]
    | undefined
): Appointment[] => {
  if (!data) {
    return [];
  }

  return data.flatMap((item) => {
    if (Array.isArray(item)) {
      return item.map(mapCalendarAppointmentsResponseToAppointment);
    }
    return [];
  });
};

export const isHiddenActivity = (
  item: CoWorkerCalendarItem,
  hideAbsenceBlocks?: boolean
) => {
  if (item.appointmentType === "scheduleActivity") {
    if (hideAbsenceBlocks && isAbsenceScheduleActivity(item)) {
      return true;
    }
  }
  return false;
};

export const getUserEmailId = (
  appointmentFilters: CalenderAppointmentsSearchFilters
) => {
  if (appointmentFilters.tableView === "AGENDA") {
    return appointmentFilters.agendaCalendar.emailId?.join(",");
  }

  if (appointmentFilters.tableView === "OVERVIEW") {
    return appointmentFilters.overviewCalendar.emailId?.join(",");
  }

  return undefined;
};

export const isScheduleActivity = (
  data: OutlookEvent | Task | Appointment | CoWorkerScheduleActivity
): data is CoWorkerScheduleActivity => {
  return (
    "appointmentType" in data && data.appointmentType === "scheduleActivity"
  );
};

type FilterSchedulesProps = Pick<Settings, "calendar"> & {
  schedules: Schedule[] | undefined;
  calendarFilters: CalendarFilters;
  appointmentFilters: CalenderAppointmentsSearchFilters;
};

export const filterSchedules = ({
  schedules,
  calendar,
  calendarFilters,
  appointmentFilters,
}: FilterSchedulesProps) => {
  if (!schedules || calendarFilters.hideScheduleBlocks) {
    return [];
  }

  const filterAbsence = calendar?.hide_absence_blocks
    ? schedules?.filter((item) => item.shiftType !== "ABSENCE")
    : schedules;

  const filterHiddenActivity = calendarFilters.hideHiddenActivity
    ? filterAbsence.filter((item) => {
        const config = calendar?.activity_configuration?.find(
          (a) => a.activity_key === item.activity
        );

        return config?.hide_activity !== true;
      })
    : filterAbsence;

  const displaySchedules = [...filterHiddenActivity];

  const filtered =
    appointmentFilters.calendarFilters.filteredScheduleTypes ?? [];
  const filteredUpper = new Set(filtered.map((t) => t.toUpperCase()));

  if (filteredUpper.size === 0) {
    return displaySchedules;
  }

  return displaySchedules?.filter((s) => {
    const typeKey = (s.shiftType ?? s.activity)?.toUpperCase() ?? "";
    return filteredUpper.has(typeKey);
  });
};
